import AdvanceTable from "components/common/advance-table/AdvanceTable";
import useAdvanceTable from "hooks/useAdvanceTable";
import AdvanceTableProvider from "providers/AdvanceTableProvider";
import TablePaginationFooter from "components/common/TablePaginationFooter";
import React, { useState, useEffect } from "react";
import { Alert, Dropdown, Spinner } from "react-bootstrap";
import { TagDetailsModal } from "./TagDetailsModal";
import useTags from "hooks/tags/useTags";
import { TagUpdateModal } from "./TagUpdateModal";
import { TagCreateModal } from "./TagCreateModal";
import { toast } from "react-toastify";
import WidgetSectionTitle from "widgets/WidgetSectionTitle";
import CardDropdown from "components/common/CardDropdown";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import SubtleBadge from "components/common/SubtleBadge";
import useFormattedDate from "hooks/useFormatDate";
import { ConfirmDeleteModal } from "components/common/ConfirmDeleteModal";
import IconButton from "components/common/IconButton";
import { TagManager } from "./TagManager";
import TagFilter from "./TagFilter";

// Re-export TagManager for external use
export { TagManager, TagFilter };

export const Tags = () => {
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(20);

  const {
    tags,
    loading,
    error,
    totalPages,
    totalElements,
    fetchTagById,
    selectedTag,
    setSelectedTag,
    updateTag,
    deleteTag,
    createTag,
  } = useTags(page, pageSize);

  const [showModal, setShowModal] = useState(false);
  const [showUpdateModal, setShowUpdateModal] = useState(false);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [tagToDelete, setTagToDelete] = useState(null);

  // Handle page/pageSize changes
  useEffect(() => {
    // This will trigger a refetch when page or pageSize changes
  }, [page, pageSize]);

  const handleViewTag = async (id) => {
    try {
      await fetchTagById(id);
      setShowModal(true);
    } catch (error) {
      toast.error(error, { theme: "colored" });
    }
  };

  const handleUpdateTag = async (id) => {
    try {
      await fetchTagById(id);
      setShowUpdateModal(true);
    } catch (error) {
      toast.error(error, { theme: "colored" });
    }
  };

  const handleCreateTag = () => {
    setShowCreateModal(true);
  };

  const confirmDelete = (id) => {
    setTagToDelete(id);
    setShowDeleteModal(true);
  };

  const handleDelete = async () => {
    await deleteTag(tagToDelete);
  }

  const formatText = (text, maxLength = 50) => {
    if (!text) return "-";
    return text.length > maxLength ? `${text.slice(0, maxLength)}...` : text;
  };

  const { formatDateTime } = useFormattedDate();

  const columns = [
    {
      accessorKey: "name",
      header: "Name",
      meta: {
        headerProps: { className: "text-900" },
      },
    },
    {
      accessorKey: "description",
      header: "Description",
      cell: ({ getValue }) => formatText(getValue()),
      meta: {
        headerProps: { className: "text-900" },
      },
    },
    {
      id: "actions",
      header: "Actions",
      cell: ({ row }) => {
        const item = row.original;
        return (
          <CardDropdown drop="start">
            <div className="py-2">
              <Dropdown.Item onClick={() => handleViewTag(item.id)}>
                View
              </Dropdown.Item>
              <Dropdown.Item onClick={() => handleUpdateTag(item.id)}>
                Edit
              </Dropdown.Item>
              <Dropdown.Divider as="div" />
              <Dropdown.Item
                onClick={() => confirmDelete(item.id)}
                className="text-danger"
              >
                Delete
              </Dropdown.Item>
            </div>
          </CardDropdown>
        );
      },
      meta: {
        headerProps: { className: "text-900 text-center" },
        cellProps: { className: "text-center" },
      },
    },
   
  ];

  const table = useAdvanceTable({
    data: tags,
    columns,
    sortable: true,
    selection: true,
    pagination: true,
    perPage: 10,
  });

  if (loading && tags.length === 0) {
    return (
      <div
        className="d-flex justify-content-center align-items-center"
        style={{ minHeight: 400 }}
      >
        <Spinner animation="border" variant="success" />
        <span className="ms-3" style={{ fontSize: "13.33px" }}>
          Loading...
        </span>
      </div>
    );
  }

  if (error) {
    return (
      <Alert variant="danger">
        <Alert.Heading style={{ fontSize: 16 }}>
          <FontAwesomeIcon icon="exclamation-triangle" className="me-2" />
          Error
        </Alert.Heading>
        <p style={{ fontSize: "13.33px" }}>{error.message}</p>
      </Alert>
    );
  }

  return (
    <>
      <WidgetSectionTitle
        icon="tags"
        title="Tags"
        subtitle="Manage tags for categorizing content and resources."
        transform="shrink-2"
        className="mb-4 mt-3"
      />

      <div className="d-flex justify-content-between mb-2 flex-wrap">
        <div></div>
        <div className="mt-3 mt-lg-0">
          <IconButton
            variant="falcon-default"
            size="sm"
            icon="plus"
            transform="shrink-3"
            onClick={handleCreateTag}
            className="text-xs me-3"
          >
            <span className="ms-1">Create Tag</span>
          </IconButton>
        </div>
      </div>

      <AdvanceTableProvider {...table}>
        <AdvanceTable
          headerClassName="bg-300 text-nowrap align-middle"
          rowClassName="align-middle white-space-nowrap"
          tableProps={{
            bordered: true,
            striped: true,
            className: "fs-10 mb-0 overflow-hidden",
          }}
        />
        <div className="mt-3">
          <TablePaginationFooter
            page={page}
            totalPages={totalPages}
            totalElements={totalElements}
            onPrev={() => setPage((prev) => Math.max(prev - 1, 0))}
            onNext={() =>
              setPage((prev) => Math.min(prev + 1, totalPages - 1))
            }
            pageSize={pageSize}
            onPageSizeChange={(size) => {
              setPageSize(size);
              setPage(0);
            }}
            pageSizeOptions={[10, 50, 100]}
          />
        </div>
      </AdvanceTableProvider>

      {/* Modals */}
      <TagDetailsModal
        selectedTag={selectedTag}
        loading={loading}
        showModal={showModal}
        setShowModal={setShowModal}
      />

      <TagUpdateModal
        selectedTag={selectedTag}
        loading={loading}
        showUpdateModal={showUpdateModal}
        setShowUpdateModal={setShowUpdateModal}
        updateTag={updateTag}
      />

      <TagCreateModal
        loading={loading}
        showCreateModal={showCreateModal}
        setShowCreateModal={setShowCreateModal}
        createTag={createTag}
      />

      <ConfirmDeleteModal
        show={showDeleteModal}
        setShow={setShowDeleteModal}
        onConfirm={handleDelete}
        itemName="tag"
        loading={loading}
      />
    </>
  );
};